/**
 * NowID Profile Service
 * Handles getting profile data and customer type information
 */

import axios from 'axios';
import SophieCustomerTypeService from './SophieCustomerTypeService';
import { NOWID_PROXY_URL } from '../config';

const getProxyCandidates = () => {
  const configured = [NOWID_PROXY_URL, '/api/nowid-proxy.php', '/api/nowid-proxy'].filter(Boolean);
  const expanded = [];

  configured.forEach((url) => {
    if (expanded.includes(url)) return;
    expanded.push(url);

    if (url.endsWith('.php')) {
      const withoutPhp = url.replace(/\.php$/, '');
      if (!expanded.includes(withoutPhp)) expanded.push(withoutPhp);
    } else if (!expanded.includes(`${url}.php`)) {
      expanded.push(`${url}.php`);
    }
  });

  return expanded;
};

const postViaProxy = async (body) => {
  const candidates = getProxyCandidates();
  let lastError;

  for (const proxyUrl of candidates) {
    try {
      return await axios.post(proxyUrl, body);
    } catch (error) {
      lastError = error;
      const status = error?.response?.status;
      if (status === 404 || status === 405 || status === 502 || !status) {
        continue;
      }
      throw error;
    }
  }

  throw lastError;
};

/**
 * Get profile data by NPID
 * @param {string} npid - The NPID from token verification
 * @returns {Promise} Profile data response
 */
export const getProfile = async (npid) => {
  try {
    const response = await postViaProxy({
      proxyTarget: 'nowid',
      endpoint: 'api/getprofile',
      method: 'POST',
      payload: {
        npid: npid,
        mode: 'bynpid',
      },
    });

    console.log('✅ getprofile response:', response.data);

    return response.data;
  } catch (error) {
    console.error('❌ Error getting profile:', error);
    throw error;
  }
};

/**
 * Update profile data by NPID
 * @param {string} npid - The NPID from token verification
 * @param {object} profileUpdates - Fields to update on profile
 * @returns {Promise} Update profile response
 */
export const updateProfile = async (npid, profileUpdates = {}) => {
  try {
    const response = await postViaProxy({
      proxyTarget: 'nowid',
      endpoint: 'api/updateprofile',
      method: 'POST',
      payload: {
        npid,
        mode: 'bynpid',
        ...profileUpdates,
      },
    });

    return response.data;
  } catch (error) {
    console.error('❌ Error updating profile:', error);
    throw error;
  }
};

/**
 * Determine customer type based on profile data
 * @param {object} profileData - Profile data object
 * @returns {Promise<object>} Customer type information
 */
export const determineCustType = async (profileData) => {
  const fsa = profileData?.fsa || '';
  const fsaType = profileData?.fsaType || '';
  const novaXIndicator = profileData?.novaXIndicator || false;

  let custType = 'not-binded';
  let readableCustType = 2; // Default: not-binded

  if (fsa !== '') {
    if (fsaType === 'OTT') {
      custType = 'ott';
      readableCustType = 8;
    } else if (fsaType === 'TV' && novaXIndicator === true) {
      custType = 'r2stb';
      readableCustType = 9;
    } else {
      // For TV type without novaXIndicator or other types
      // Call SBIMS service to get actual Sophie status
      try {
        const sophieData = await SophieCustomerTypeService.checkCustomerType(fsa);
        const mapped = SophieCustomerTypeService.mapSubStatusToCustomerType(sophieData.subStatus);
        custType = mapped.cust_type;
        readableCustType = mapped.readable_cust_type;
      } catch (e) {
        console.warn('Failed to fetch Sophie customer type, falling back to default TV:', e);
        custType = 'tv';
        readableCustType = 7;
      }
    }
  } else {
    custType = "not-binded";
    readableCustType = 2;
  }

  return {
    custType,
    readableCustType,
  };
};

/**
 * Build complete customer info object from token and profile data
 * @param {string} token - Session token
 * @param {object} tokenData - Token verification response data
 * @param {object} profileData - Profile response data
 * @returns {Promise<object>} Complete customer information
 */
export const buildCustInfo = async (token, tokenData, profileData) => {
  const tokenPayload = tokenData?.data || {};
  const profilePayload = profileData?.data || {};
  const mergedPayload = { ...tokenPayload, ...profilePayload };
  const { custType, readableCustType } = await determineCustType(mergedPayload);

  return {
    token,
    // Token info
    nowid: mergedPayload?.nowid || '',
    npid: tokenPayload?.npid || mergedPayload?.npid || '',
    nickname: mergedPayload?.englishName || mergedPayload?.nickname || '',
    englishName: mergedPayload?.englishName || '',
    // Customer data
    email: mergedPayload?.email || '',
    mobile: mergedPayload?.mobile || '',
    fsa: mergedPayload?.fsa || '',
    fsaType: mergedPayload?.fsaType || '',
    novaXIndicator: mergedPayload?.novaXIndicator || false,
    // Verification timestamps
    mobileVerifiedDate: mergedPayload?.mobileVerifiedDate || '',
    verifiedDate: mergedPayload?.verifiedDate || '',
    // Customer type
    cust_type: custType,
    readable_cust_type: readableCustType,
    // Status indicators
    isLoggedIn: true,
    accountStatus: mergedPayload?.accountStatus || '',
  };
};
